import { useSelector } from "react-redux";
import { RootState } from "../../store";
import React, { useEffect, useState } from "react";

import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";

import Errors, { getFieldErrors } from "../../models/errors";
import {
  ReceivedDocument,
  newReceivedDocument,
  DOCUMENT_TYPE_CHOICES,
  DOCUMENT_TYPE_INVOICE,
  CREATION_TYPE_MANUAL,
  CREATION_TYPE_CDC,
  SALE_TYPE_CASH,
  SALE_TYPE_CASH_LABEL,
  SALE_TYPE_CREDIT,
  SALE_TYPE_CREDIT_LABEL,
} from "../../models/received-document";

import { useHistory } from "react-router-dom";
import { errorAlert, successAlert } from "../utils/messages";

import {
  ItemRequestStatus,
  createItem,
  getItem,
  updateItem,
} from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import {
  CButton,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
  CCol,
  CFormGroup,
  CInput,
  CInputRadio,
  CLabel,
  CRow,
  CSelect,
  CTextarea,
} from "@coreui/react";

import { FieldErrors } from "../form/FieldErrors";
import Loading from "../indicators/Loading";
import NotFound from "../indicators/NotFound";
import ServerError from "../indicators/ServerError";

import { CURRENCY_CHOICES, PYG } from "../../currency/available-currencies";
import CurrencyField from "../currencies/CurrencyField";
import PYGField from "../currencies/PYGField";
import { changeTZToPyg, changeTZToUtc } from "../../utils/dates";
import { emptyValueOnUndefined } from "../../utils/fields";
import Supplier from "../../models/supplier";
import SupplierSelect from "../supplier/SupplierSelect";
import { getRoutePath } from "../../routes";
import { Spinner } from "react-bootstrap";

// ─── Shared style helpers ───────────────────────────────────────────────────

const SectionDivider: React.FC<{ icon: string; label: string }> = ({
  icon,
  label,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      marginBottom: "16px",
      marginTop: "16px",
    }}
  >
    <div
      style={{
        width: "4px",
        height: "18px",
        background: "#006657",
        borderRadius: "2px",
        marginRight: "10px",
        flexShrink: 0,
      }}
    />
    <i
      className={`fa ${icon} text-primary mr-2`}
      style={{ fontSize: "0.85rem" }}
    />
    <h6
      style={{
        margin: 0,
        fontWeight: 600,
        color: "var(--theme-muted)",
        fontSize: "0.78rem",
        textTransform: "uppercase",
        letterSpacing: "0.8px",
      }}
    >
      {label}
    </h6>
    <div
      style={{
        flex: 1,
        height: "1px",
        background: "var(--theme-divider)",
        marginLeft: "10px",
      }}
    />
  </div>
);

const FormLabel: React.FC<{
  required?: boolean;
  children: React.ReactNode;
}> = ({ required, children }) => (
  <CLabel
    className="mb-0"
    style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}
  >
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

// ─── IVA helpers ─────────────────────────────────────────────────────────────

const calcIva10 = (amount10: number): number => {
  const raw = amount10 / 11;
  return Math.floor(raw * 100) / 100;
};

const calcIva5 = (amount5: number): number => {
  const raw = (amount5 * 5) / 105;
  return Math.floor(raw * 100) / 100;
};

// ─── Component ───────────────────────────────────────────────────────────────

interface ReceivedDocumentFormProps {
  readonly?: boolean;
}

const ReceivedDocumentForm: React.FC<ReceivedDocumentFormProps> = ({
  readonly,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadNotFound, setLoadNotFound] = useState(false);

  const [editingDocument, setEditingDocument] =
    useState<ReceivedDocument | null>(newReceivedDocument(taxPayer.id));

  const [documentDate, setDocumentDate] = useState<Date | undefined>(
    undefined,
  );
  const [currency, setCurrency] = useState(PYG);
  const [selectedSupplier, setSelectedSupplier] = useState<
    Supplier | undefined
  >(undefined);

  const history = useHistory();

  // ── helpers ──────────────────────────────────────────────────────────────

  const getEditingDocumentId = () => {
    const urlParams = new URLSearchParams(history.location.search);
    const id = urlParams.get("id");
    if (id !== null && !isNaN(parseInt(id))) {
      return parseInt(id);
    }
    return undefined;
  };

  const isPyg = currency === PYG;

  const recalcTotals = (
    doc: ReceivedDocument,
    a10?: number,
    a5?: number,
    exempt?: number,
  ): Partial<ReceivedDocument> => {
    const v10 = a10 ?? doc.amount10Percent ?? 0;
    const v5 = a5 ?? doc.amount5Percent ?? 0;
    const vExempt = exempt ?? doc.amountExempt ?? 0;

    const iva10 = v10 > 0 ? calcIva10(v10) : undefined;
    const iva5 = v5 > 0 ? calcIva5(v5) : undefined;
    const taxed10 = iva10 !== undefined ? v10 - iva10 : undefined;
    const taxed5 = iva5 !== undefined ? v5 - iva5 : undefined;
    const total = v10 + v5 + vExempt;

    return {
      iva10Percent: iva10,
      iva5Percent: iva5,
      taxed10Percent: taxed10,
      taxed5Percent: taxed5,
      amount: total > 0 ? total : undefined,
    };
  };

  // ── onChange handlers ─────────────────────────────────────────────────────

  const onSupplierChange = (newSupplier: Supplier | null) => {
    setSelectedSupplier(newSupplier !== null ? newSupplier : undefined);
    setEditingDocument({
      ...editingDocument,
      supplierId: newSupplier !== null ? newSupplier.id : undefined,
      supplierSocialName:
        newSupplier !== null
          ? newSupplier.legalName || newSupplier.fantasyName
          : undefined,
      supplierFantasyName:
        newSupplier !== null ? newSupplier.fantasyName : undefined,
      supplierRuc: newSupplier !== null ? newSupplier.ruc : undefined,
    });
  };

  const onDocumentTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEditingDocument({ ...editingDocument, documentType: e.target.value });
  };

  const onDocumentNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocument({ ...editingDocument, documentNumber: e.target.value });
  };

  const onDocumentDateChange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) return;
    const date = new Date((value as moment.Moment).toISOString());
    setDocumentDate(date);
    setEditingDocument({
      ...editingDocument,
      documentDate: changeTZToUtc(date).toISOString(),
    });
  };

  const onSupplierRucChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocument({ ...editingDocument, supplierRuc: e.target.value });
  };

  const onSupplierSocialNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setEditingDocument({
      ...editingDocument,
      supplierSocialName: e.target.value,
    });
  };

  const onSupplierFantasyNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setEditingDocument({
      ...editingDocument,
      supplierFantasyName: e.target.value,
    });
  };

  const onSaleTypeChange = (newSaleType: string) => {
    setEditingDocument({ ...editingDocument, saleType: newSaleType });
  };

  const onCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocument({ ...editingDocument, currency: e.target.value });
    setCurrency(e.target.value);
  };

  const onPygExchangeRateChange = (newExchangeRate?: number) => {
    setEditingDocument({
      ...editingDocument,
      pygExchangeRate: newExchangeRate,
    });
  };

  const onAmount10PercentChange = (newAmount?: number) => {
    const derived = recalcTotals(
      editingDocument ?? {},
      newAmount,
      undefined,
      undefined,
    );
    setEditingDocument({
      ...editingDocument,
      amount10Percent: newAmount,
      ...derived,
    });
  };

  const onAmount5PercentChange = (newAmount?: number) => {
    const derived = recalcTotals(
      editingDocument ?? {},
      undefined,
      newAmount,
      undefined,
    );
    setEditingDocument({
      ...editingDocument,
      amount5Percent: newAmount,
      ...derived,
    });
  };

  const onAmountExemptChange = (newAmount?: number) => {
    const derived = recalcTotals(
      editingDocument ?? {},
      undefined,
      undefined,
      newAmount,
    );
    setEditingDocument({
      ...editingDocument,
      amountExempt: newAmount,
      ...derived,
    });
  };

  const onObsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocument({ ...editingDocument, obs: e.target.value });
  };

  // ── save ──────────────────────────────────────────────────────────────────

  const onSave = async () => {
    if (editingDocument === null) return;

    setSubmitting(true);

    const computedAmount =
      (editingDocument.amount10Percent ?? 0) +
      (editingDocument.amount5Percent ?? 0) +
      (editingDocument.amountExempt ?? 0);

    const document = {
      ...editingDocument,
      amount: computedAmount > 0 ? computedAmount : editingDocument.amount,
      clientRuc: taxPayer.ruc,
      clientSocialName: taxPayer.socialName,
      creationType: editingDocument.creationType ?? CREATION_TYPE_MANUAL,
      currency: editingDocument.currency ?? PYG,
    };

    let requestPromise: Promise<ItemRequestStatus<ReceivedDocument>>;
    let successMessage = "Documento agregado exitosamente";

    if (document.id === undefined) {
      requestPromise = createItem<ReceivedDocument>(
        "/received_documents/create/",
        document,
      );
    } else {
      successMessage = "Documento actualizado exitosamente";
      requestPromise = updateItem<ReceivedDocument>(
        `/received_documents/${document.id}/`,
        document,
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }
      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
    } else {
      successAlert(successMessage);
      history.push(getRoutePath("receivedDocuments"));
    }
  };

  // ── load ──────────────────────────────────────────────────────────────────

  const loadInitialDocumentById = async (): Promise<ReceivedDocument | null> => {
    const editingId = getEditingDocumentId();
    if (editingId === undefined) return null;

    const requestStatus = await getItem<ReceivedDocument>(
      `/received_documents/${editingId}/`,
    );

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.status === NOT_FOUND) {
        setLoadNotFound(true);
      } else {
        setLoadError(true);
      }
      return null;
    }

    if (requestStatus.data === undefined || requestStatus.data === null) {
      setLoadError(true);
      return null;
    }

    return requestStatus.data!;
  };

  const loadInitialData = async () => {
    setLoading(true);
    const document = await loadInitialDocumentById();

    if (document === null) {
      setLoading(false);
      return;
    }

    setEditingDocument(document);

    if (document.documentDate) {
      const pygDate = new Date(document.documentDate);
      const docDate = new Date(pygDate.getTime() + 3 * 60 * 60 * 1000);
      setDocumentDate(docDate);
    }

    setCurrency(document.currency ?? PYG);
    setLoading(false);
  };

  useEffect(() => {
    const newDocumentDate = changeTZToPyg(new Date());
    if (editingDocument !== null && editingDocument.id === undefined) {
      editingDocument.documentDate =
        changeTZToUtc(newDocumentDate).toISOString();
      setDocumentDate(newDocumentDate);
    }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── render ────────────────────────────────────────────────────────────────

  if (loading) return <Loading />;
  if (loadNotFound) return <NotFound />;
  if (loadError) return <ServerError />;

  const isEditing = editingDocument !== null && editingDocument.id !== undefined;
  const isCdcLocked =
    isEditing && editingDocument?.creationType === CREATION_TYPE_CDC;
  const showSaleType =
    editingDocument?.documentType === DOCUMENT_TYPE_INVOICE;
  const saleType = editingDocument?.saleType;

  return (
    <>
      <CCard className="shadow-sm border-0">
        <CCardHeader
          className="d-flex align-items-center"
          style={{
            background: "linear-gradient(180deg, #006657 0%, #095e55 100%)",
            color: "var(--theme-surface)",
            borderBottom: "none",
            padding: "14px 20px",
          }}
        >
          <i
            className="fa fa-file-text-o mr-2"
            style={{ fontSize: "1.1rem" }}
          />
          <span style={{ fontWeight: 600, fontSize: "1rem" }}>
            {isEditing ? (
              <>
                {readonly ? "Ver" : "Editar"} Documento Recibido{" "}
                <span style={{ opacity: 0.85 }}>#{editingDocument!.id}</span>
              </>
            ) : (
              <>Agregar Documento Recibido</>
            )}
          </span>
        </CCardHeader>

        <CCardBody style={{ padding: "24px" }}>
          {isCdcLocked && (
            <div className="alert alert-info mb-3" role="alert">
              Este documento fue cargado desde un CDC. Solo se puede modificar
              la observación.
            </div>
          )}

          {/* ── Datos del documento ── */}
          <fieldset disabled={readonly || isCdcLocked}>
            <SectionDivider
              icon="fa-file-text-o"
              label="Datos del Documento"
            />

            <CFormGroup>
              <CRow className="align-items-center">
                <CCol md={2}>
                  <FormLabel required>Tipo de Documento:</FormLabel>
                </CCol>
                <CCol md={4}>
                  <CSelect
                    value={emptyValueOnUndefined(editingDocument?.documentType)}
                    onChange={onDocumentTypeChange}
                  >
                    {DOCUMENT_TYPE_CHOICES.map((choice) => (
                      <option key={choice.value} value={choice.value}>
                        {choice.label}
                      </option>
                    ))}
                  </CSelect>
                  <FieldErrors
                    errors={getFieldErrors("documentType", errors) as string[]}
                  />
                </CCol>
                <CCol md={2}>
                  <FormLabel required>N° Documento:</FormLabel>
                </CCol>
                <CCol md={4}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(
                      editingDocument?.documentNumber,
                    )}
                    onChange={onDocumentNumberChange}
                    placeholder="001-001-0000001"
                  />
                  <FieldErrors
                    errors={
                      getFieldErrors("documentNumber", errors) as string[]
                    }
                  />
                </CCol>
              </CRow>
            </CFormGroup>

            <CFormGroup>
              <CRow className="align-items-center">
                <CCol md={2}>
                  <FormLabel required>Fecha:</FormLabel>
                </CCol>
                <CCol md={4}>
                  <Datetime
                    className="pl-0"
                    onChange={onDocumentDateChange}
                    value={documentDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    closeOnSelect={true}
                  />
                  <FieldErrors
                    errors={getFieldErrors("documentDate", errors) as string[]}
                  />
                </CCol>
              </CRow>
            </CFormGroup>

            {/* ── Emisor ── */}
            <SectionDivider icon="fa-building-o" label="Emisor" />

            <CFormGroup>
              <CRow className="align-items-center">
                <CCol md={2}>
                  <FormLabel>Proveedor:</FormLabel>
                </CCol>
                <CCol md={10}>
                  <SupplierSelect
                    value={
                      selectedSupplier !== undefined ? selectedSupplier : null
                    }
                    onChange={onSupplierChange}
                  />
                </CCol>
              </CRow>
            </CFormGroup>

            <CFormGroup>
              <CRow className="align-items-center">
                <CCol md={2}>
                  <FormLabel required>Razón Social:</FormLabel>
                </CCol>
                <CCol md={4}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(
                      editingDocument?.supplierSocialName,
                    )}
                    onChange={onSupplierSocialNameChange}
                    disabled={selectedSupplier !== undefined}
                  />
                  <FieldErrors
                    errors={
                      getFieldErrors("supplierSocialName", errors) as string[]
                    }
                  />
                </CCol>
                <CCol md={2}>
                  <FormLabel required>RUC:</FormLabel>
                </CCol>
                <CCol md={4}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(editingDocument?.supplierRuc)}
                    onChange={onSupplierRucChange}
                    disabled={selectedSupplier !== undefined}
                  />
                  <FieldErrors
                    errors={getFieldErrors("supplierRuc", errors) as string[]}
                  />
                </CCol>
              </CRow>
            </CFormGroup>

            <CFormGroup>
              <CRow className="align-items-center">
                <CCol md={2}>
                  <FormLabel>Nombre fantasía:</FormLabel>
                </CCol>
                <CCol md={4}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(
                      editingDocument?.supplierFantasyName,
                    )}
                    onChange={onSupplierFantasyNameChange}
                    disabled={selectedSupplier !== undefined}
                  />
                  <FieldErrors
                    errors={
                      getFieldErrors(
                        "supplierFantasyName",
                        errors,
                      ) as string[]
                    }
                  />
                </CCol>
              </CRow>
            </CFormGroup>

            {/* ── Condiciones ── */}
            <SectionDivider icon="fa-exchange" label="Condiciones" />

            {showSaleType && (
              <CFormGroup>
                <CRow className="align-items-center">
                  <CCol md={2}>
                    <FormLabel>Condición de venta:</FormLabel>
                  </CCol>
                  <CCol md="auto">
                    <div
                      style={{
                        display: "inline-flex",
                        border: "1px solid var(--theme-border)",
                        borderRadius: "6px",
                        overflow: "hidden",
                      }}
                    >
                      <label
                        htmlFor="saleTypeCash"
                        style={{
                          margin: 0,
                          padding: "6px 16px",
                          cursor: "pointer",
                          background:
                            saleType === SALE_TYPE_CASH ? "#006657" : "var(--theme-surface)",
                          color:
                            saleType === SALE_TYPE_CASH ? "white" : "var(--theme-text-soft)",
                          fontWeight: 500,
                          fontSize: "0.875rem",
                          transition: "all 0.15s",
                          borderRight: "1px solid var(--theme-border)",
                        }}
                      >
                        <CInputRadio
                          id="saleTypeCash"
                          name="saleType"
                          value={SALE_TYPE_CASH}
                          checked={saleType === SALE_TYPE_CASH}
                          onChange={() => onSaleTypeChange(SALE_TYPE_CASH)}
                          style={{ display: "none" }}
                        />
                        {SALE_TYPE_CASH_LABEL}
                      </label>
                      <label
                        htmlFor="saleTypeCredit"
                        style={{
                          margin: 0,
                          padding: "6px 16px",
                          cursor: "pointer",
                          background:
                            saleType === SALE_TYPE_CREDIT
                              ? "#006657"
                              : "var(--theme-surface)",
                          color:
                            saleType === SALE_TYPE_CREDIT
                              ? "white"
                              : "var(--theme-text-soft)",
                          fontWeight: 500,
                          fontSize: "0.875rem",
                          transition: "all 0.15s",
                        }}
                      >
                        <CInputRadio
                          id="saleTypeCredit"
                          name="saleType"
                          value={SALE_TYPE_CREDIT}
                          checked={saleType === SALE_TYPE_CREDIT}
                          onChange={() => onSaleTypeChange(SALE_TYPE_CREDIT)}
                          style={{ display: "none" }}
                        />
                        {SALE_TYPE_CREDIT_LABEL}
                      </label>
                    </div>
                    <FieldErrors
                      errors={getFieldErrors("saleType", errors) as string[]}
                    />
                  </CCol>
                </CRow>
              </CFormGroup>
            )}

            <CFormGroup>
              <CRow className="align-items-center">
                <CCol md={2}>
                  <FormLabel required>Moneda:</FormLabel>
                </CCol>
                <CCol md={2}>
                  <CSelect
                    value={emptyValueOnUndefined(editingDocument?.currency)}
                    onChange={onCurrencyChange}
                  >
                    {Array.from(CURRENCY_CHOICES.entries()).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </CSelect>
                  <FieldErrors
                    errors={getFieldErrors("currency", errors) as string[]}
                  />
                </CCol>
                {currency !== PYG && (
                  <>
                    <CCol md={2}>
                      <FormLabel required>Tipo de Cambio:</FormLabel>
                    </CCol>
                    <CCol md={3}>
                      <CurrencyField
                        currency={currency}
                        value={editingDocument?.pygExchangeRate}
                        onChange={onPygExchangeRateChange}
                      />
                      <FieldErrors
                        errors={
                          getFieldErrors(
                            "pygExchangeRate",
                            errors,
                          ) as string[]
                        }
                      />
                    </CCol>
                  </>
                )}
              </CRow>
            </CFormGroup>

            {/* ── Totales ── */}
            <SectionDivider icon="fa-calculator" label="Resumen de Totales" />

            <div
              style={{
                background: "var(--theme-highlight)",
                border: "1px solid var(--theme-highlight-border)",
                borderRadius: "8px",
                padding: "16px 20px",
                marginBottom: "16px",
              }}
            >
              {/* Total general — auto-calculado */}
              <CFormGroup>
                <CRow className="align-items-center">
                  <CCol md={2}>
                    <CLabel
                      className="mb-0"
                      style={{
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        color: "var(--theme-accent)",
                      }}
                    >
                      Total:
                    </CLabel>
                  </CCol>
                  <CCol md={3}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: "var(--theme-accent)",
                      }}
                    >
                      {isPyg ? (
                        <PYGField
                          value={editingDocument?.amount}
                          disabled={true}
                        />
                      ) : (
                        <CurrencyField
                          currency={currency}
                          value={editingDocument?.amount}
                          disabled={true}
                        />
                      )}
                    </div>
                    <FieldErrors
                      errors={getFieldErrors("amount", errors) as string[]}
                    />
                  </CCol>
                </CRow>
              </CFormGroup>

              {/* Subtotales por alícuota */}
              <CFormGroup>
                <CRow className="align-items-center">
                  <CCol md={2}>
                    <FormLabel>Total c/IVA 10%:</FormLabel>
                  </CCol>
                  <CCol md={2}>
                    {isPyg ? (
                      <PYGField
                        value={editingDocument?.amount10Percent}
                        onChange={onAmount10PercentChange}
                      />
                    ) : (
                      <CurrencyField
                        currency={currency}
                        value={editingDocument?.amount10Percent}
                        onChange={onAmount10PercentChange}
                      />
                    )}
                    <FieldErrors
                      errors={
                        getFieldErrors("amount10Percent", errors) as string[]
                      }
                    />
                  </CCol>
                  <CCol md={2}>
                    <FormLabel>Total c/IVA 5%:</FormLabel>
                  </CCol>
                  <CCol md={2}>
                    {isPyg ? (
                      <PYGField
                        value={editingDocument?.amount5Percent}
                        onChange={onAmount5PercentChange}
                      />
                    ) : (
                      <CurrencyField
                        currency={currency}
                        value={editingDocument?.amount5Percent}
                        onChange={onAmount5PercentChange}
                      />
                    )}
                    <FieldErrors
                      errors={
                        getFieldErrors("amount5Percent", errors) as string[]
                      }
                    />
                  </CCol>
                  <CCol md={2}>
                    <FormLabel>Exento:</FormLabel>
                  </CCol>
                  <CCol md={2}>
                    {isPyg ? (
                      <PYGField
                        value={editingDocument?.amountExempt}
                        onChange={onAmountExemptChange}
                      />
                    ) : (
                      <CurrencyField
                        currency={currency}
                        value={editingDocument?.amountExempt}
                        onChange={onAmountExemptChange}
                      />
                    )}
                    <FieldErrors
                      errors={
                        getFieldErrors("amountExempt", errors) as string[]
                      }
                    />
                  </CCol>
                </CRow>
              </CFormGroup>

              {/* IVA — auto-calculado */}
              <CFormGroup className="mb-0">
                <CRow className="align-items-center">
                  <CCol md={2}>
                    <FormLabel>IVA 10%:</FormLabel>
                  </CCol>
                  <CCol md={2}>
                    {isPyg ? (
                      <PYGField
                        value={editingDocument?.iva10Percent}
                        disabled={true}
                        decimalsLimit={2}
                      />
                    ) : (
                      <CurrencyField
                        currency={currency}
                        value={editingDocument?.iva10Percent}
                        disabled={true}
                      />
                    )}
                    <FieldErrors
                      errors={
                        getFieldErrors("iva10Percent", errors) as string[]
                      }
                    />
                  </CCol>
                  <CCol md={2}>
                    <FormLabel>IVA 5%:</FormLabel>
                  </CCol>
                  <CCol md={2}>
                    {isPyg ? (
                      <PYGField
                        value={editingDocument?.iva5Percent}
                        disabled={true}
                        decimalsLimit={2}
                      />
                    ) : (
                      <CurrencyField
                        currency={currency}
                        value={editingDocument?.iva5Percent}
                        disabled={true}
                      />
                    )}
                    <FieldErrors
                      errors={
                        getFieldErrors("iva5Percent", errors) as string[]
                      }
                    />
                  </CCol>
                </CRow>
              </CFormGroup>
            </div>
          </fieldset>

          {/* ── Observación — siempre editable salvo readonly ── */}
          <fieldset disabled={readonly}>
            <SectionDivider icon="fa-comment-o" label="Observaciones" />
            <CFormGroup>
              <CRow className="align-items-center">
                <CCol md={2}>
                  <FormLabel>Observación:</FormLabel>
                </CCol>
                <CCol md={10}>
                  <CTextarea
                    value={emptyValueOnUndefined(editingDocument?.obs)}
                    onChange={onObsChange}
                    rows={3}
                    style={{ resize: "vertical", minHeight: "70px" }}
                  />
                  <FieldErrors
                    errors={getFieldErrors("obs", errors) as string[]}
                  />
                </CCol>
              </CRow>
            </CFormGroup>
          </fieldset>
        </CCardBody>

        <CCardFooter
          style={{
            background: "var(--theme-surface-raised)",
            borderTop: "1px solid var(--theme-divider)",
            padding: "14px 20px",
          }}
        >
          {!readonly ? (
            <div style={{ display: "flex", gap: "10px" }}>
              <CButton
                color="primary"
                onClick={onSave}
                disabled={submitting}
                style={{ minWidth: "130px", fontWeight: 600, padding: "8px 20px" }}
              >
                {submitting ? (
                  <Spinner
                    animation="grow"
                    style={{
                      height: "17px",
                      width: "17px",
                      marginTop: "auto",
                      marginBottom: "auto",
                      marginRight: "10px",
                    }}
                  />
                ) : (
                  <i className="fa fa-save mr-2" />
                )}
                {submitting ? "Guardando..." : "Guardar"}
              </CButton>
              <CButton
                color="secondary"
                onClick={() =>
                  history.push(getRoutePath("receivedDocuments"))
                }
                style={{ fontWeight: 600, padding: "8px 20px" }}
              >
                Cancelar
              </CButton>
            </div>
          ) : (
            <></>
          )}
        </CCardFooter>
      </CCard>
    </>
  );
};

export default ReceivedDocumentForm;
